/* eslint-disable react/no-danger */
import React from 'react';
import type { WCardSuiteSummaryView } from '@zola/svc-web-api-ts-client';
import { FAQPage, WithContext, Question } from 'schema-dts';
import { Faq } from './types';

export const ZOLA_DOMAIN = 'https://www.zola.com';

interface JsonLdScriptProps {
  structuredData: WithContext<FAQPage>;
}

export const JsonLdScript: React.FC<JsonLdScriptProps> = ({ structuredData }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
  />
);

const baseStructuredData: WithContext<any> = {
  '@context': 'https://schema.org',
};

export const mapFaqItem = (faqItem: Faq): Question => ({
  '@type': 'Question',
  name: faqItem.question,
  acceptedAnswer: {
    '@type': 'Answer',
    text: faqItem.answer,
  },
});

/**
 * FAQ Documentation
 * @see https://developers.google.com/search/docs/data-types/faqpage
 * @example
 * const faqStructuredData = getFaqStructuredData(FAQ_LD.homepage);
 */
export const getFaqStructuredData = (faqArray: Faq[]): WithContext<FAQPage> => ({
  ...baseStructuredData,
  '@type': 'FAQPage',
  mainEntity: faqArray.map(mapFaqItem),
});

export interface Collection {
  uuid: string;
  card: string;
  color: string;
}

export const SaveTheDateCollections: Collection[] = [
  {
    uuid: '65fc8699-607c-485f-a678-74d51036e367',
    card: 'Brevard',
    color: 'White',
  },
  {
    uuid: '27ef0dfc-0d46-4bff-b8ee-3ff9ae016878',
    card: 'Eugene',
    color: 'Grayed Navy',
  },
  {
    uuid: '0f7d36dc-f36b-4ffb-ac01-a9328e0cfadc',
    card: 'Abbey',
    color: 'Black',
  },
];

export const InvitationCollections: Collection[] = [
  {
    uuid: '02a90a35-ced6-4092-9ee5-033f154046c3',
    card: 'Goundry',
    color: 'Light Oatmeal',
  },
  {
    uuid: '4eb0e383-e860-45c7-8777-a138b843c309',
    card: 'Ybel',
    color: 'Mint',
  },
  {
    uuid: '38417e71-7f1e-4d0b-864b-7ddaef491c9f',
    card: 'Brevard',
    color: 'Dark Emerald',
  },
];

export const matchingCollection = (collection: Collection, card: WCardSuiteSummaryView): boolean =>
  Boolean(
    (collection.uuid && collection.uuid === card.uuid) ||
      (card.name && new RegExp(collection.card, 'i').test(card.name))
  );

export const getCardDefaultColor = (collections: Collection[]) => (
  suite?: WCardSuiteSummaryView
): string => {
  if (!suite?.lead_card?.colors) return '';

  const collection = collections.find(_ => matchingCollection(_, suite));

  if (collection) {
    const color = suite.lead_card.colors.find(
      _ => _.label && new RegExp(collection.color, 'i').test(_.label)
    );
    if (color) return color.value || '';
  }

  // Found not thing return the first color
  const [defaultColor] = suite.lead_card.colors;
  return defaultColor.value || '';
};

export const sortSuitesByCollections = (collections: Collection[]) => (
  aSuite: WCardSuiteSummaryView,
  bSuite: WCardSuiteSummaryView
): number => {
  const { lead_card: aCard } = aSuite;
  const { lead_card: bCard } = bSuite;

  if (!aCard && !bCard) return 0;

  if (!aCard) return -1;
  if (!bCard) return -1;

  const aOrder =
    collections.findIndex(collection => matchingCollection(collection, aCard)) + 1 || 0;

  const bOrder =
    collections.findIndex(collection => matchingCollection(collection, bCard)) + 1 || 0;

  return aOrder - bOrder;
};
